/**
 * CustomDataGridDesktop Component
 *
 * Desktop version of the data grid using React Admin's Datagrid.
 * The RaDatagrid-root itself is the horizontal-scroll container: it stays
 * at 100% of its parent, its inner `<table>` sizes to content width
 * (`min-width: max-content`), and `overflow-x: auto` on the root produces
 * the scrollbar when the table exceeds the viewport — matches Angular
 * DataTables UX.
 *
 * Why overflow is on the Datagrid root, not on a wrapper `<Box>`:
 *   A wrapper Box sees `RaDatagrid-root` as its only child. RA sets that
 *   root to `width: 100%`, so the Box thinks content fits — no scrollbar
 *   triggers. Putting overflow on the root itself lets the inner table
 *   (which DOES grow past 100%) trigger the scrollbar directly.
 */

import React from 'react';
import { Datagrid, DatagridProps, RaRecord } from 'react-admin';

interface CustomDataGridDesktopProps<RecordType extends RaRecord = any>
  extends DatagridProps<RecordType> {
  children: React.ReactNode;
}

const CustomDataGridDesktop = <RecordType extends RaRecord = any>({
  children,
  expand,
  rowClick,
  ...props
}: CustomDataGridDesktopProps<RecordType>) => {
  return (
    <Datagrid
      {...props}
      expand={expand}
      bulkActionButtons={false}
      rowClick={rowClick !== undefined ? rowClick : expand ? 'expand' : 'show'}
      sx={{
        width: '100%',
        overflowX: 'auto',
        '& .MuiTable-root': {
          minWidth: 'max-content',
        },
        '& .MuiTableCell-root, & .MuiTableCell-root *': {
          whiteSpace: 'nowrap !important',
        },

        '& .RaDatagrid-headerCell': {
          fontWeight: 600,
        },
        '& .RaDatagrid-row': {
          cursor: 'pointer',
        },
      }}
    >
      {children}
    </Datagrid>
  );
};

export default CustomDataGridDesktop;
